import React, { useCallback } from 'react';
import { Button, Icon } from '@material-ui/core';
import useStyles from 'app/cluster-styles/useStyles';
import { IMAGE_TYPE } from '../../util/enums';
import SectionCard from './SectionCard';
import NodeGroupCard from './NodeGroupCard';

/**
 * Generic compute nodes section.
 * Owns the SectionCard, Add/Remove group CRUD, and NodeGroupCard mapping.
 * Provider-specific fields are rendered via the `renderGroupFields` prop.
 *
 * @param {object}   form                - flat form state
 * @param {function} setField            - form field setter
 * @param {boolean}  disabled            - disable all inputs
 * @param {number}   maxGroups           - max number of groups allowed (default 10)
 * @param {object}   newGroupTemplate    - template for new groups (merged with defaults)
 * @param {function} renderGroupFields   - (group, index, { updateGroup, groups }) => JSX
 * @param {string}   tooltip             - optional tooltip for the section card
 */
export default function ComputeNodesSection({
    form, setField, disabled, maxGroups = 10,
    newGroupTemplate, renderGroupFields, tooltip,
}) {
    const classes = useStyles();
    const groups = form.nodeGroups || [];

    const addGroup = useCallback(() => {
        if (groups.length >= maxGroups) return;
        const template = {
            name: `compute-${groups.length + 1}`,
            group_node_prefix_label: `Node Group ${groups.length + 1}`,
            count: 1,
            instance_id: '',
            image_type: IMAGE_TYPE.HEIDI_STACK,
            ...newGroupTemplate,
        };
        setField('nodeGroups', [...groups, template]);
    }, [groups, maxGroups, newGroupTemplate, setField]);

    const removeGroup = useCallback((index) => {
        if (groups.length <= 1) return;
        setField('nodeGroups', groups.filter((_, i) => i !== index));
    }, [groups, setField]);

    const updateGroup = useCallback((index, field, value) => {
        const updated = [...groups];
        updated[index] = { ...updated[index], [field]: value };
        setField('nodeGroups', updated);
    }, [groups, setField]);

    return (
        <SectionCard
            icon="device_hub"
            title="Compute Nodes"
            tooltip={tooltip || "The available Instance Sizes and quantity of Instances may vary based on your provider account status."}
            action={
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    onClick={addGroup}
                    disabled={groups.length >= maxGroups || disabled}
                    className={classes.grayButton}
                >
                    Add Node Group
                </Button>
            }
        >
            {groups.map((group, index) => (
                <NodeGroupCard
                    key={`node-group-${index}`}
                    index={index}
                    label={group.group_node_prefix_label || `Node Group ${index + 1}`}
                    onRemove={() => removeGroup(index)}
                    canRemove={groups.length > 1}
                >
                    {renderGroupFields(group, index, { updateGroup, groups })}
                </NodeGroupCard>
            ))}
        </SectionCard>
    );
}
